import { IS_DEV } from '@/utils/env';
import { getStoredCompanyKey } from '@/services/api/erpat-client';

/**
 * Note-label registry ids for ERPat, configured per tenant.
 *
 * ERPat stores a note's labels as a CSV of REGISTRY IDS and renders its chips
 * by looking each id up. Verified from the web's own save payload:
 * `labels: Journal,143` — the title Syntry had written, plus the id the web
 * appended. A title written by Syntry is stored verbatim, fails to decode as an
 * id, and shows no chip.
 *
 * There is no labels endpoint in `/v1/api`, so ids can't be fetched. The only
 * source is `VITE_ERPAT_LABEL_IDS` — explicit, hand-checked, scoped per tenant.
 * An unconfigured tenant sends titles, exactly as before this file existed: no
 * chip, but never a WRONG label.
 *
 * **Why there is no discovery.** An earlier version learned `title -> id` from
 * notes carrying one of each ("Journal,143"). That shape is not evidence of
 * equivalence: it is identical whether 143 means Journal or Urgent, because a
 * label applied by hand in the web sits beside the title Syntry wrote without
 * being related to it. A wrong pairing then rode into every later save — a
 * Journal note stored as Urgent, in ERPat's own module too, permanently.
 * Requiring the pairing to be consistent across notes only makes the same guess
 * repeatedly; it never makes it true. Restoring an inference needs an
 * authoritative signal, which this API does not offer.
 *
 * THE DURABLE FIX IS SERVER-SIDE: have `POST`/`PATCH /me/notes` resolve label
 * titles against the registry (creating missing ones) the way the web's own
 * form does. Then Syntry sends `"Journal"` and it works everywhere, and this
 * file can be deleted.
 */

/** `title (lower) -> registry id`. */
type LabelMap = Record<string, string>;

/** `companyKey (lower) -> title (lower) -> id`; '' keys the any-tenant bucket. */
const CONFIGURED: Map<string, LabelMap> = (() => {
  const raw = import.meta.env.VITE_ERPAT_LABEL_IDS;
  const byTenant = new Map<string, LabelMap>();
  if (typeof raw !== 'string' || !raw.trim()) return byTenant;

  for (const entry of raw.split(',')) {
    const [scope, rest] = entry.includes('|') ? entry.split('|') : ['', entry];
    const [title, id] = (rest ?? '').split(':');
    const tenant = (scope ?? '').trim().toLowerCase();
    const key = title?.trim().toLowerCase();
    const value = id?.trim();
    if (!key || !value || !/^\d+$/.test(value)) continue;

    const bucket = byTenant.get(tenant) ?? {};
    bucket[key] = value;
    byTenant.set(tenant, bucket);
  }
  return byTenant;
})();

function tenantKey(): string {
  return (getStoredCompanyKey() ?? '').trim().toLowerCase();
}

/** The buckets that may answer for the signed-in tenant, most specific first. */
function buckets(): LabelMap[] {
  return [CONFIGURED.get(tenantKey()) ?? {}, CONFIGURED.get('') ?? {}];
}

/** Ids already reported as unmapped in dev — see {@link labelTitleFor}. */
const reportedIds = new Set<string>();

// ── Lookup ───────────────────────────────────────────────────────────
/**
 * The registry id for a label title on the SIGNED-IN tenant, or '' when it
 * isn't known. Callers fall back to sending the title unchanged.
 */
export function labelIdFor(title: string): string {
  const key = title.trim().toLowerCase();
  if (!key) return '';

  for (const bucket of buckets()) {
    const id = bucket[key];
    if (id) return id;
  }
  return '';
}

/**
 * The label TITLE for a registry id, or '' when unknown — the reverse of
 * {@link labelIdFor}.
 *
 * Notes labelled in the ERPat web arrive as bare ids ("143"), which are
 * meaningless to a person and defeat kind detection. Where the tenant has
 * mapped them, those resolve back to "Journal" and such a note reads correctly;
 * where it hasn't, the id is hidden rather than guessed at.
 */
export function labelTitleFor(id: string): string {
  const key = id.trim();
  if (!key) return '';

  for (const bucket of buckets()) {
    for (const [title, value] of Object.entries(bucket)) {
      if (value === key) return title;
    }
  }

  // Once per id per session: this runs for every label on every note mapped, so
  // logging each call would bury the console on a tenant with no config at all.
  if (IS_DEV && /^\d+$/.test(key) && !reportedIds.has(key)) {
    reportedIds.add(key);
    // eslint-disable-next-line no-console
    console.info(
      `[labels] no title configured for id ${key} on tenant "${tenantKey()}" — ` +
        'add it to VITE_ERPAT_LABEL_IDS to show its chip.',
    );
  }
  return '';
}
